import { z } from "zod";
import { DEFAULT_DATE_RANGE_DAYS } from "../entities/dateRange.js";
import { teammateIdSchema } from "../entities/teammate.js";
const dateFilterSchema = z.object({
  after: z.iso.datetime({ offset: true }).describe("Include interactions recorded after this ISO date"),
  before: z.iso.datetime({ offset: true }).describe("Include interactions recorded before this ISO date")
});
const optionalPartialDateFilterSchema = z.object({
  after: z.iso.datetime({ offset: true }).nullish(),
  before: z.iso.datetime({ offset: true }).nullish()
}).nullish().describe(
  `Include interactions recorded within the specified date range, or null to use the default (last ${DEFAULT_DATE_RANGE_DAYS} days).`
);
const optionalTeammatesFilterSchema = z.array(teammateIdSchema).readonly().nullish().describe(
  "Teammate IDs (UUIDs) to filter by, or null to include all teammates."
);
const optionalIngestedDateFilterSchema = z.object({
  after: z.iso.datetime({ offset: true }).nullish(),
  before: z.iso.datetime({ offset: true }).nullish()
}).nullish().describe(
  "Optional ingested-date filter: when a completed recording became available in the system, distinct from `date` (when the call happened). Rarely needed \u2014 usually pass null. AND-combined with `date`: it only narrows to calls matching both bounds, never adds calls outside the `date` range. Its main use is the ingestion-lag pattern: on a very frequent run, widen `date` and set `ingested_date.after` to the last run to pick up recordings that arrived late without re-analyzing earlier calls. Each bound is a full ISO datetime with offset, inclusive; provide after, before, or both. Null for no filter."
);
const playbookCriterionFilterSchema = z.union([
  z.object({
    value_type: z.literal("yes_no").describe("Criteria value type"),
    id: z.uuid().describe("Criteria ID (UUID)"),
    value: z.enum(["yes", "no"]).describe("Criteria value")
  }),
  z.object({
    value_type: z.literal("1_5_score").describe("Criteria value type"),
    id: z.uuid().describe("Criteria ID (UUID)"),
    op: z.enum(["=", "<", "<=", ">", ">="]).describe("Comparison operator"),
    value: z.enum(["1", "2", "3", "4", "5"]).describe("Criteria score (1-5)")
  }),
  z.object({
    value_type: z.enum(["yes_no", "1_5_score"]).describe(
      "Criteria value type \u2014 must match the criterion's grading type"
    ),
    id: z.uuid().describe("Criteria ID (UUID)"),
    op: z.enum(["is_applicable", "is_not_applicable"]).describe(
      "Applicability predicate. 'is_applicable' matches only interactions where this criterion was actually graded \u2014 i.e. the call reached the phase where the criterion is in play \u2014 and excludes interactions where it came back N/A (e.g. short voicemails that hung up before the relevant part of the call). 'is_not_applicable' matches the inverse."
    )
  })
]);
const optionalPlaybookCriteriaFilterSchema = z.array(playbookCriterionFilterSchema).nullish().describe(
  "Playbook criteria values/applicability to filter by, or null for no filter."
);
const customFieldNameSchema = z.string().describe("Custom field name");
const customFieldFilterSchema = z.union([
  z.object({
    field_name: customFieldNameSchema,
    op: z.literal("equals").optional().describe(
      "Exact string equality. May be omitted for backward compatibility."
    ),
    value: z.string().describe("Expected exact custom field value")
  }),
  z.object({
    field_name: customFieldNameSchema,
    op: z.enum(["=", "<", "<=", ">", ">="]).describe("Numeric comparison operator"),
    value: z.number().describe("Numeric comparison threshold")
  }),
  z.object({
    field_name: customFieldNameSchema,
    op: z.enum(["after", "before"]).describe(
      "Calendar-day comparison for fields of type date or date_and_time"
    ),
    value: z.iso.date().describe("Calendar date (YYYY-MM-DD); the named day itself is excluded")
  }),
  z.object({
    field_name: customFieldNameSchema,
    op: z.literal("within").describe(
      "Inclusive calendar-day range for fields of type date or date_and_time"
    ),
    start: z.iso.date().describe("First included day (YYYY-MM-DD)"),
    end: z.iso.date().describe("Last included day (YYYY-MM-DD)")
  })
]);
const numericCustomFieldFilterDescription = "Numeric comparison operators parse string-backed values after removing optional dollar, pound, or euro signs, commas, and whitespace. Values that still cannot be parsed as numbers do not match.";
const dateCustomFieldFilterDescription = "Date operators (after, before, within) apply only to fields of type date or date_and_time; date_and_time values are compared by their calendar day in the company time zone.";
const optionalContactCustomFieldsFilterSchema = z.array(customFieldFilterSchema).nullish().describe(
  `Contact custom field values to filter interactions by, matched via the interaction's linked contact. Omit op or use 'equals' for exact string equality. ${numericCustomFieldFilterDescription} ${dateCustomFieldFilterDescription} Only use field names listed as contact custom fields. Null for no filter.`
);
const optionalCustomerInteractionCustomFieldsFilterSchema = z.array(customFieldFilterSchema).nullish().describe(
  `Customer interaction custom field values to filter by, set directly on the interaction. Omit op or use 'equals' for exact string equality. ${numericCustomFieldFilterDescription} ${dateCustomFieldFilterDescription} Only use field names listed as customer interaction custom fields. Null for no filter.`
);
const optionalOutcomeFilterSchema = z.object({
  type: z.enum(["preset", "custom"]).describe("Outcome type"),
  id: z.string().describe("Outcome ID"),
  value: z.string().describe("Outcome value")
}).nullish().describe("Outcome to filter by, or null for any outcome.");
const signalMetadataFilterSchema = z.union([
  z.object({
    type: z.literal("enum").describe("Enum metadata field"),
    op: z.literal("equals").describe("Equality match"),
    field_id: z.uuid().describe("Metadata field ID (UUID)"),
    value: z.string().describe("Expected enum value")
  }),
  z.object({
    type: z.literal("number").describe("Numeric metadata field"),
    op: z.literal("range").describe("Half-open range [lower, upper)"),
    field_id: z.uuid().describe("Metadata field ID (UUID)"),
    lower: z.number().describe("Inclusive lower bound"),
    upper: z.number().describe("Exclusive upper bound")
  }),
  z.object({
    type: z.literal("number").describe("Numeric metadata field"),
    op: z.enum(["<", "<=", ">", ">=", "="]).describe("Comparison operator"),
    field_id: z.uuid().describe("Metadata field ID (UUID)"),
    value: z.number().describe("Threshold value")
  })
]);
const signalFilterSchema = z.object({
  id: z.uuid().describe("Signal ID (UUID)"),
  metadata_filters: z.array(signalMetadataFilterSchema).nullish().describe(
    "Optional metadata field constraints applied to this signal's matches. Multiple entries are AND-combined."
  )
});
const optionalSignalsFilterSchema = z.array(signalFilterSchema).nullish().describe(
  "Signals to filter by. Multiple entries are AND-combined: an interaction must have matched every listed signal (and satisfy each entry's metadata filters). Pass null/omit for no signal filtering."
);
const optionalCallDurationFilterSchema = z.array(
  z.object({
    op: z.enum(["<", "<=", ">", ">="]).describe("Comparison operator against the call's duration"),
    value_seconds: z.number().int().nonnegative().describe("Duration threshold in seconds")
  })
).nullish().describe(
  "Filter interactions by total call recording duration, in seconds. Each entry is AND-combined, so pass two entries to express a band (e.g. `>=` 30 and `<` 120 for calls between 30 seconds and 2 minutes). Useful for isolating ultra-short calls. Note this measures raw recording length and does not by itself distinguish a live pickup from a voicemail or no-answer \u2014 combine it with playbook-criteria applicability filters when you need calls where a real conversation started. Null for no duration filter."
);
const contactNameFilterSchema = z.string().max(150).nullish().describe(
  "Customer/contact name to filter interactions by. Matching interactions are found via fuzzy name search, so minor spelling differences are tolerated. Use the name exactly as the user wrote it \u2014 do not infer, expand, or add alternates. Use the same value in both count-customer-interactions and research-customer-interactions calls."
);
const MAX_TRANSCRIPT_SEARCH_CHARS = 300;
const MAX_TRANSCRIPT_SEARCH_QUERIES = 4;
const transcriptSearchSchema = z.object({
  queries: z.array(z.string().max(MAX_TRANSCRIPT_SEARCH_CHARS)).min(1).max(MAX_TRANSCRIPT_SEARCH_QUERIES).describe(
    "Search text variants for transcript chunks. Provide one query for a narrow search, or 2-4 variants when the same concept could appear in several phrasings. An interaction matches if it matches any query; duplicate interactions are collapsed before normal filters are applied.\n- keyword: each individual query requires all of its tokens to appear. Use multiple queries for alternate wording, spelling, acronyms, or expanded names.\n- semantic: each query should be a concrete transcript-like phrase a caller or customer might actually say, not analysis about the transcript.\n- hybrid: use when you have a specific term to anchor the search, but relevant calls may describe the same topic without saying that exact phrase."
  ),
  type: z.enum(["keyword", "semantic", "hybrid"]).describe(
    "Transcript search mode. Pick one:\n- keyword: exact-term BM25 search. Use when the user gave distinctive words that should appear verbatim in the transcript.\n- semantic: meaning-based vector search. Use for concepts, themes, sentiment, objections, confusion, excitement, or behavioral questions; this is the default when the user did not provide exact words.\n- hybrid: combined keyword and semantic ranking using the same query text for both. Use when you have a specific term to anchor the search, but relevant calls may describe the same topic without saying that exact phrase."
  )
}).optional().describe(
  "Optional transcript pre-filter. Narrows interactions to recordings with matching transcript chunks, then applies the other interaction filters. Provide `type` and `queries`. Do not include date, teammate, outcome, signal, or custom-field constraints in transcript queries; use the normal filters for those. Use the same `transcript_search` value in both count-customer-interactions and research-customer-interactions calls."
);
export {
  MAX_TRANSCRIPT_SEARCH_CHARS,
  MAX_TRANSCRIPT_SEARCH_QUERIES,
  contactNameFilterSchema,
  dateFilterSchema,
  optionalCallDurationFilterSchema,
  optionalContactCustomFieldsFilterSchema,
  optionalCustomerInteractionCustomFieldsFilterSchema,
  optionalIngestedDateFilterSchema,
  optionalOutcomeFilterSchema,
  optionalPartialDateFilterSchema,
  optionalPlaybookCriteriaFilterSchema,
  optionalSignalsFilterSchema,
  optionalTeammatesFilterSchema,
  transcriptSearchSchema
};
//# sourceMappingURL=customerInteractionFilters.js.map
